import {
  useState,
  useRef,
  useEffect,
  useCallback,
  FormEvent,
  KeyboardEvent,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { FileAttachment } from '../attachment/FileAttachment';
import type { FileAttachment as FileAttachmentType } from '../../types';
import { SendIcon } from '../icons';
import { Button, Input, Textarea, Text, Box } from '@chakra-ui/react';

const TEXTAREA_MIN_H = 10;
const TEXTAREA_MAX_H = 40;

interface PromptInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Submit時に呼ばれるコールバック。valueにはSubmit時点の入力値が渡される */
  onSubmit: (value: string, attachedFiles?: FileAttachmentType[]) => void;
  placeholder: string;
  disabled: boolean;
  buttonTitle: string;
  maxLength?: number;
  autoFocus?: boolean;
  useTextarea?: boolean;
  // ファイル添付機能
  enableFileAttachment?: boolean;
  maxFiles?: number;
  acceptedFileTypes?: string[];
  isUploading?: boolean;
  // 外部から追加されたファイル（ドラッグ&ドロップ用）
  droppedFiles?: FileAttachmentType[];
  onDroppedFilesProcessed?: () => void;
  // ファイル添付エラーを親に通知
  onFileError?: (error: string) => void;
}

export interface PromptInputRef {
  focus: () => void;
}

export const PromptInput = forwardRef<PromptInputRef, PromptInputProps>(
  (
    {
      value,
      onChange,
      onSubmit,
      placeholder,
      disabled,
      buttonTitle,
      maxLength = 500,
      autoFocus = true,
      useTextarea = false,
      enableFileAttachment = false,
      maxFiles = 5,
      acceptedFileTypes,
      isUploading = false,
      droppedFiles,
      onDroppedFilesProcessed,
      onFileError,
    },
    ref
  ) => {
    const [isComposing, setIsComposing] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState<FileAttachmentType[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const focusInputIfVisible = useCallback(() => {
      const target = useTextarea ? textareaRef.current : inputRef.current;
      if (!target) return;
      if (target.closest('[aria-hidden="true"]') || target.closest('[inert]')) return;
      target.focus();
    }, [useTextarea]);

    // 外部からフォーカスを制御できるようにする
    useImperativeHandle(
      ref,
      () => ({
        focus: focusInputIfVisible,
      }),
      [focusInputIfVisible]
    );

    const handleFilesSelected = (files: FileAttachmentType[]) => {
      setAttachedFiles((prev) => [...prev, ...files]);
    };

    const clearAttachedFiles = () => {
      setAttachedFiles([]);
    };

    // 外部からドロップされたファイルを追加
    useEffect(() => {
      if (droppedFiles && droppedFiles.length > 0) {
        // ファイル数の上限をチェック
        const remainingSlots = maxFiles - attachedFiles.length;
        if (remainingSlots > 0) {
          const filesToAdd = droppedFiles.slice(0, remainingSlots);
          setAttachedFiles((prev) => [...prev, ...filesToAdd]);
        }
        // 処理完了を通知
        onDroppedFilesProcessed?.();
      }
    }, [droppedFiles, onDroppedFilesProcessed, maxFiles, attachedFiles.length]);

    // マウント時に自動フォーカス
    useEffect(() => {
      if (autoFocus && !disabled) {
        focusInputIfVisible();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 非活性から活性に戻ったタイミングでフォーカス
    useEffect(() => {
      if (!disabled) {
        focusInputIfVisible();
      }
    }, [disabled, focusInputIfVisible]);

    const handleSubmit = (e: FormEvent) => {
      e.preventDefault();
      // テキスト入力は必須、かつ文字数上限以内
      if (value.trim() && value.length <= maxLength && !disabled && !isUploading) {
        // Submit直後に入力をクリア（非同期処理の完了を待たない）
        const currentValue = value;
        const currentFiles = attachedFiles.length > 0 ? attachedFiles : undefined;
        onChange('');
        clearAttachedFiles();
        onSubmit(currentValue, currentFiles);
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // IME入力中（日本語変換中）の場合はサブミットしない
      if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
        e.preventDefault();
        // テキスト入力は必須、かつ文字数上限以内
        if (value.trim() && value.length <= maxLength && !disabled && !isUploading) {
          // Submit直後に入力をクリア（非同期処理の完了を待たない）
          const currentValue = value;
          const currentFiles = attachedFiles.length > 0 ? attachedFiles : undefined;
          onChange('');
          clearAttachedFiles();
          onSubmit(currentValue, currentFiles);
        }
      }
    };

    const handleCompositionStart = () => {
      setIsComposing(true);
    };

    const handleCompositionEnd = () => {
      setIsComposing(false);
    };

    const isDisabled = disabled || isUploading;
    // テキスト入力は必須（ファイル添付だけではサブミット不可）、かつ文字数上限以内
    const hasTextContent = !!value.trim();
    const isWithinLimit = value.length <= maxLength;
    const canSubmit = hasTextContent && isWithinLimit;

    // 文字数の状態に応じたスタイルを取得
    const getCharacterCountStyle = () => {
      const ratio = value.length / maxLength;
      if (ratio >= 1)
        return {
          fontSize: 'xs',
          color: 'semantics.error',
          fontWeight: 'semibold',
          transition: 'all 0.2s',
        };
      if (ratio >= 0.8)
        return {
          fontSize: 'xs',
          color: 'semantics.warning',
          fontWeight: 'medium',
          transition: 'all 0.2s',
        };
      return { fontSize: 'xs', color: 'gray.400', transition: 'all 0.2s' };
    };

    const removeAttachedFile = (index: number) => {
      setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    return (
      <Box as="form" mx="auto" w="full" maxW="chat" p={0} onSubmit={handleSubmit}>
        {/* 添付ファイルリストを入力エリアの上に表示 */}
        {attachedFiles.length > 0 && (
          <Box mb={2} display="flex" flexWrap="wrap" gap={2} p={0}>
            {attachedFiles.map((file, index) => (
              <Box
                key={index}
                display="flex"
                maxW={200}
                minW={100}
                alignItems="center"
                gap={2}
                borderRadius="md"
                border="1px solid"
                borderColor="gray.400"
                bg="gray.50"
                px={3}
                py={1}
                transition="colors 0.2s"
                _hover={{ bg: 'gray.100' }}
              >
                <Text fontSize="sm" truncate flex="1" minW="0">
                  {file.filename}
                </Text>
                <Button
                  variant="secondary"
                  type="button"
                  size="sm"
                  onClick={() => removeAttachedFile(index)}
                  disabled={isDisabled}
                  p={1.5}
                  minW="auto"
                >
                  ✕
                </Button>
              </Box>
            ))}
          </Box>
        )}
        <Box
          position="relative"
          display="flex"
          flexDirection="column"
          gap={1}
          borderRadius={{ base: '2xl', md: 'xl' }}
          border="2px solid"
          borderColor="gray.400"
          bg="white"
          px={{ base: 3, md: 3 }}
          py={{ base: 2, md: 2 }}
          boxShadow="sm"
          transition="all 0.25s ease"
          _focusWithin={{
            borderColor: 'border.primary',
            bg: 'white',
            boxShadow: 'focusRing',
          }}
          _hover={{
            borderColor: 'gray.500',
            boxShadow: 'md',
          }}
        >
          <Box display="flex" w="full" alignItems="flex-end" gap={2}>
            {enableFileAttachment && (
              <FileAttachment
                onFilesSelected={handleFilesSelected}
                currentFileCount={attachedFiles.length}
                disabled={isDisabled}
                maxFiles={maxFiles}
                acceptedTypes={acceptedFileTypes}
                onError={onFileError}
              />
            )}
            {useTextarea ? (
              <Textarea
                ref={textareaRef}
                value={value}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                placeholder={placeholder}
                disabled={isDisabled}
                rows={1}
                maxLength={maxLength}
                variant="unstyled"
                size="md"
                autoresize
                minH={TEXTAREA_MIN_H}
                maxH={TEXTAREA_MAX_H}
                flex="1"
                resize="none"
                py={2}
                fontWeight="normal"
                lineHeight="1.5"
                transitionProperty="background-color, border-color, box-shadow, opacity"
              />
            ) : (
              <Input
                ref={inputRef}
                type="text"
                value={value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                placeholder={placeholder}
                disabled={isDisabled}
                maxLength={maxLength}
                variant="unstyled"
                size="md"
                h={15}
                minH={15}
                flex="1"
                fontWeight="normal"
                lineHeight="1.5"
              />
            )}
            <Button
              type="submit"
              disabled={isDisabled || !canSubmit}
              variant="primary"
              size="md"
              minW={11}
              minH={11}
              w={11}
              h={11}
              p={0}
              flexShrink={0}
              title={buttonTitle}
              aria-label={buttonTitle}
              aria-busy={isUploading}
              aria-live="polite"
            >
              {isUploading ? (
                <Box
                  w={6}
                  h={6}
                  animation="spin"
                  borderRadius="full"
                  border="2px solid"
                  borderColor="gray.200"
                  borderTopColor="borders.default"
                  aria-hidden="true"
                />
              ) : (
                <SendIcon width={24} height={24} aria-hidden="true" />
              )}
            </Button>
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="flex-end"
          pr={1}
          pt={1}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <Text {...getCharacterCountStyle()}>
            {value.length}/{maxLength}
            <Text as="span" srOnly>
              {value.length >= maxLength
                ? ` 文字数が上限に達しました`
                : ` 残り${maxLength - value.length}文字`}
            </Text>
          </Text>
        </Box>
      </Box>
    );
  }
);

PromptInput.displayName = 'PromptInput';
